import React from 'react';
import {Listbox} from '@headlessui/react';
import {ChevronDown} from 'lucide-react';
import {ProcessedBiomarker} from '../../utils/biomarkerUtils';
import {cleanBiomarkerName} from '../../utils/biomarkerUtils';

interface BiomarkerSelectProps {
    biomarkers: ProcessedBiomarker[];
    selectedBiomarker: ProcessedBiomarker | null;
    onSelect: (biomarker: ProcessedBiomarker) => void;
}

const BiomarkerSelect: React.FC<BiomarkerSelectProps> = ({biomarkers, selectedBiomarker, onSelect}) => {
    if (biomarkers.length === 0) return null;

    return (
        <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto">
            {/* Label with search icon - hidden on mobile */}
            <div className="hidden sm:flex items-center gap-2 text-mld-muted-text text-sm">
                <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8" />
                    <path d="M21 21l-4.35-4.35" />
                </svg>
                <span>Select A Biomarker</span>
            </div>

            {/* Dropdown */}
            <Listbox value={selectedBiomarker ?? undefined} onChange={onSelect}>
                {({open}) => (
                    <div className="relative w-full sm:w-auto">
                        <Listbox.Button
                            className={`relative flex items-stretch justify-between gap-0 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-mld-primary focus:ring-opacity-50 border border-mld-muted-text w-full sm:min-w-[250px] ${
                                open
                                    ? 'bg-mld-menu-background text-mld-card-text rounded-t-lg border-b-0'
                                    : 'bg-mld-background text-mld-muted-text rounded-lg hover:bg-opacity-80'
                            }`}>
                            <span className="text-left flex-1 px-3 sm:px-4 py-2 truncate">
                                {selectedBiomarker ? cleanBiomarkerName(selectedBiomarker.testName) : 'Select Biomarker'}
                            </span>
                            <div className="flex items-center justify-center px-3 border-l border-mld-muted-text">
                                <ChevronDown className="w-4 h-4 flex-shrink-0" />
                            </div>
                        </Listbox.Button>

                        <Listbox.Options className="absolute right-0 left-0 w-full bg-mld-menu-background rounded-b-lg border border-mld-muted-text max-h-60 overflow-auto focus:outline-none z-[100]">
                            {biomarkers.map((biomarker) => (
                                <Listbox.Option
                                    key={biomarker.testName}
                                    value={biomarker}
                                    className={({active, selected}) =>
                                        `cursor-pointer px-3 sm:px-4 py-3 text-sm ${
                                            selected
                                                ? 'bg-mld-menu-background text-mld-card-text font-medium border-t border-mld-muted-text'
                                                : active
                                                  ? 'bg-mld-background text-mld-card-text'
                                                  : 'text-mld-card-text'
                                        }`
                                    }>
                                    {({selected}) => (
                                        <span className={selected ? 'font-medium' : 'font-normal'}>
                                            {cleanBiomarkerName(biomarker.testName)}
                                        </span>
                                    )}
                                </Listbox.Option>
                            ))}
                        </Listbox.Options>
                    </div>
                )}
            </Listbox>
        </div>
    );
};

export default BiomarkerSelect;
